import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  SubleaseSortOrderEnum,
  SubleaseSortFieldEnum,
  SubleaseStatusEnum,
  ISubleaseList,
  ISubleaseFilters,
  ISubleaseApplication,
  IHistoricTenant,
  ISubleaseSettings,
} from '../../models/Sublease'
import { DEFAULT_PAGE_TAKE } from 'shared/constants/filters'

const subleaseSlice = createSlice({
  name: 'sublease',
  initialState: {
    list: {
      data: null as ISubleaseList | null,
      loading: false,
      error: null,
      filters: {
        sortColumn: SubleaseSortFieldEnum.fromDate,
        sortorder: SubleaseSortOrderEnum.desc,
        take: DEFAULT_PAGE_TAKE,
        skip: 0,
        statuses: [SubleaseStatusEnum.ongoing],
      } as ISubleaseFilters,
    },
    details: {
      application: {
        data: null as ISubleaseApplication | null,
        loading: false,
        error: null,
        processing: false,
        processingError: null,
        errorMessage: null,
      },
      historicalTenants: {
        data: null as IHistoricTenant[] | null,
        loading: false,
        error: null,
      },
    },
    settings: {
      data: null as ISubleaseSettings | null,
      error: null,
      loading: false,
    },
  },
  reducers: {
    // Handle payload (_) for redux-saga to pickup
    fetchSubleaseList: (state, _: PayloadAction<any>) => {
      state.list.loading = true
    },
    fetchSubleaseListSuccess: (state, action: PayloadAction<ISubleaseList>) => {
      state.list.loading = false
      state.list.error = null
      state.list.data = action.payload
    },
    fetchSubleaseListFailed: (state, action: PayloadAction<any>) => {
      state.list.loading = false
      state.list.error = action.payload
      state.list.data = null
    },
    updateFilters: (state, action: PayloadAction<ISubleaseFilters>) => {
      state.list.filters = action.payload
      state.list.loading = true
    },
    fetchSubleaseDetails: (state, _: PayloadAction<any>) => {
      state.details.application.loading = true
      state.details.application.error = null

      state.details.historicalTenants.data = null
      state.details.historicalTenants.error = null
      state.details.historicalTenants.loading = false
    },
    fetchSubleaseDetailsSuccess: (
      state,
      action: PayloadAction<ISubleaseApplication>
    ) => {
      state.details.application.loading = false
      state.details.application.data = action.payload
    },
    fetchSubleaseDetailsFailed: (state, action: PayloadAction<any>) => {
      state.details.application.loading = false
      state.details.application.data = null
      state.details.application.error = action.payload
    },
    resetSubleaseDetails: (state) => {
      state.details.application.data = null
      state.details.application.loading = false
      state.details.application.error = null
    },
    fetchHistoricalTenantsRequested: (state, _: PayloadAction<any>) => {
      state.details.historicalTenants.error = null
      state.details.historicalTenants.loading = true
    },
    fetchHistoricalTenantsSuccess: (
      state,
      action: PayloadAction<IHistoricTenant[]>
    ) => {
      state.details.historicalTenants.data = action.payload
      state.details.historicalTenants.loading = false
    },
    fetchHistoricalTenantsFailed: (state, action: PayloadAction<any>) => {
      state.details.historicalTenants.error = action.payload
      state.details.historicalTenants.loading = false
    },

    addSubleaseDecisionRequested: (state, _: PayloadAction<any>) => {
      state.details.application.processing = true
      state.details.application.processingError = null
    },
    addSubleaseDecisionSuccess: (state) => {
      state.details.application.processing = false
    },
    addSubleaseDecisionFailed: (state, action: PayloadAction<any>) => {
      state.details.application.processing = false
      state.details.application.processingError = action.payload
    },

    undoSubleaseDecisionRequested: (state, _: PayloadAction<any>) => {
      state.details.application.processing = true
      state.details.application.processingError = null
    },
    undoSubleaseDecisionSuccess: (state) => {
      state.details.application.processing = false
    },
    undoSubleaseDecisionFailed: (state, action: PayloadAction<any>) => {
      state.details.application.processing = false
      state.details.application.processingError = action.payload
      state.details.application.errorMessage = action.payload
    },

    clearProcessingError: (state) => {
      state.details.application.processingError = null
    },

    updateEndDateRequested: (state, _: PayloadAction<any>) => {
      state.details.application.processing = true
      state.details.application.processingError = null
    },
    updateEndDateSuccess: (state) => {
      state.details.application.processing = false
    },
    updateEndDateFailed: (state, action: PayloadAction<any>) => {
      state.details.application.processing = false
      state.details.application.processingError = action.payload
    },

    fetchSubleaseSettingsRequested: (state, _: PayloadAction<string>) => {
      state.settings.loading = true
      state.settings.data = null
      state.settings.error = null
    },
    fetchSubleaseSettingsSuccess: (
      state,
      action: PayloadAction<ISubleaseSettings>
    ) => {
      state.settings.loading = false
      state.settings.data = action.payload
    },
    fetchSubleaseSettingsFailed: (state, action: PayloadAction<any>) => {
      state.settings.loading = false
      state.settings.data = null
      state.settings.error = action.payload
    },
  },
})

export default subleaseSlice
